// ============================================================
// THE FOCUS - who the action is about, on the display (plan map-display,
// Track C; the Focus rulings of 2026-10-07).
//
//   - IN FOCUS: the actor, the target, anyone within FOCUS_RADIUS_M of either
//     (the ruling's 8 m; the demo drew it at 2.5 m), and whoever is under the
//     pointer. The quiet map dims the rest; the two-shot keeps all of them.
//   - A LONG SHOT: a target further than LONG_SHOT_M from the actor drops out
//     of the frame (the demo's 25 m); an edge chevron points at it instead.
//
// Pure. Zero React dependency.
// ============================================================
import { centroid } from '../battlefield/solids';

/** Anyone this near the actor or the target, on the ground, is part of the action. */
export const FOCUS_RADIUS_M = 8;
/** A target further than this from the actor is not framed with it. */
export const LONG_SHOT_M = 25;
/** The edge chevron sits this far inside the view. */
const EDGE_INSET = 28;

interface Placed {
  id: string;
  x: number;
  z: number;
}

const apart = (a: { x: number; z: number }, b: { x: number; z: number }) => Math.hypot(a.x - b.x, a.z - b.z);

/**
 * Who is in focus, or null when nobody acts (then nothing is dimmed): the
 * actor, the target, anyone within FOCUS_RADIUS_M of either, and `hoveredId`.
 */
export function focusIds(
  tokens: readonly Placed[],
  actorId: string | null,
  targetId: string | null,
  hoveredId: string | null = null,
): Set<string> | null {
  const centres = tokens.filter((t) => t.id === actorId || t.id === targetId);
  if (!centres.length) return null;
  const near = (t: Placed) => centres.some((c) => apart(c, t) <= FOCUS_RADIUS_M);
  const ids = new Set(tokens.filter(near).map((t) => t.id));
  if (hoveredId) ids.add(hoveredId);
  return ids;
}

/** Is this target near enough the actor to share its frame? */
export const framesTarget = (actor: { x: number; z: number }, target: { x: number; z: number }) =>
  apart(actor, target) <= LONG_SHOT_M;

/** Where the edge chevron sits for a point off the view, and which way it points (degrees, 0 = right). */
export interface EdgeMark {
  x: number;
  y: number;
  angle: number;
}

/**
 * The chevron for a screen point outside the view: on the line from the
 * view's centre towards it, where that line meets the view inset by
 * EDGE_INSET; null for a point on screen.
 */
export function edgeMark(p: { x: number; y: number }, view: { width: number; height: number }): EdgeMark | null {
  const onScreen = p.x >= 0 && p.x <= view.width && p.y >= 0 && p.y <= view.height;
  if (onScreen) return null;
  const cx = view.width / 2;
  const cy = view.height / 2;
  const dx = p.x - cx;
  const dy = p.y - cy;
  // How far along the line the inset rectangle is reached: the nearer of its two walls.
  const reach = Math.min(Math.abs((cx - EDGE_INSET) / (dx || 1e-9)), Math.abs((cy - EDGE_INSET) / (dy || 1e-9)));
  return { x: cx + dx * reach, y: cy + dy * reach, angle: (Math.atan2(dy, dx) * 180) / Math.PI };
}

/** Where the action is on the ground: between the actor and the target, or the actor alone; null with neither. */
export function actionPoint(
  tokens: Readonly<Record<string, { x: number; z: number; elevation: number }>>,
  actorId: string | null,
  targetId: string | null,
): { x: number; y: number; z: number } | null {
  const at = [actorId, targetId].flatMap((id) => (id && tokens[id] ? [tokens[id]] : []));
  return at.length ? centroid(at.map((t) => ({ x: t.x, y: t.elevation, z: t.z }))) : null;
}

/** These markers, the absent skipped: the figures the x-ray must never lose. */
export function actionFigures<T>(
  tokens: Readonly<Record<string, T>>,
  ids: readonly (string | null | undefined)[],
): T[] {
  return ids.flatMap((id) => (id && tokens[id] ? [tokens[id]] : []));
}

/** Terrain this far from the action, on the ground, is drawn at FAR_TERRAIN (the Focus demo's "fine near, coarse far"). */
export const TERRAIN_FADE_M = 30;
export const FAR_TERRAIN = 0.45;

/**
 * How strongly terrain at `p` is drawn: 1 at the action, easing to
 * FAR_TERRAIN by TERRAIN_FADE_M away. It does not depend on the camera, so a
 * renderer works it out once per terrain and action, not once per frame.
 */
export function fadeAt(p: { x: number; z: number }, action: { x: number; z: number }): number {
  return 1 - (1 - FAR_TERRAIN) * Math.min(1, apart(p, action) / TERRAIN_FADE_M);
}
